import { useEffect, useRef, useState } from 'react';

export interface MessageMenuAction {
  id: string;
  label: string;
  onSelect: () => void;
  danger?: boolean;
}

/**
 * A setinha no canto da bolha e o menu que ela abre — o mesmo gesto do WhatsApp
 * Web, que é onde quem atende aprendeu a responder e apagar.
 *
 * O menu abre para cima quando a bolha está perto do fim da conversa: aberto para
 * baixo, ele ficaria cortado pela área que rola, justo na mensagem mais recente —
 * a que mais se quer corrigir.
 */
export function MessageMenu({
  actions,
  align,
}: {
  actions: MessageMenuAction[];
  /** Lado da bolha: o menu cresce para dentro da conversa, não para fora dela. */
  align: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);
  const [upward, setUpward] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (actions.length === 0) return null;

  const toggle = () => {
    if (!open) {
      const node = rootRef.current;
      const scroller = node?.closest('.thread__messages');
      if (node && scroller) {
        // Espaço para quatro itens, com folga para a borda.
        const room = scroller.getBoundingClientRect().bottom - node.getBoundingClientRect().bottom;
        setUpward(room < 44 * actions.length + 16);
      }
    }
    setOpen((current) => !current);
  };

  return (
    <div className={`bubble-menu bubble-menu--${align}${open ? ' bubble-menu--open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="bubble-menu__toggle"
        aria-label="Opções da mensagem"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>

      {open && (
        <div className={`bubble-menu__list${upward ? ' bubble-menu__list--up' : ''}`} role="menu">
          {actions.map((action) => (
            <button
              key={action.id}
              type="button"
              role="menuitem"
              className={`bubble-menu__item${action.danger ? ' bubble-menu__item--danger' : ''}`}
              onClick={() => {
                setOpen(false);
                action.onSelect();
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Copia texto para a área de transferência, também dentro do iframe do CRM.
 *
 * A API moderna (`navigator.clipboard`) depende de o sistema que embute o painel
 * liberar `clipboard-write` no iframe — o que não se controla daqui. Recusada, a
 * cópia cai no caminho antigo, um campo escondido selecionado e `execCommand`,
 * que funciona em qualquer iframe desde que venha de um clique.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    try {
      return document.execCommand('copy');
    } catch {
      return false;
    } finally {
      field.remove();
    }
  }
}
